import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Inbox, ShoppingCart, CalendarCheck, ShieldCheck, LogOut, Briefcase, UserSquare2, GraduationCap, Sparkles, Settings, BarChart3, Building2, PieChart, Users, HeartHandshake } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

// role: "chaplain" | "admin" | "owner"
const NAV = [
  { to: "/care-portal/admin/dashboard", label: "Overview", icon: LayoutDashboard, key: null, end: true, roles: ["owner","admin","chaplain"] },
  { to: "/care-portal/admin/analytics", label: "Website Analytics", icon: BarChart3, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/bookings", label: "Organizational Bookings", icon: CalendarCheck, key: "bookings", roles: ["owner","admin"] },
  { to: "/care-portal/admin/grief-appointments", label: "Grief Appointments", icon: HeartHandshake, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/bulk-orders", label: "Bulk Orders", icon: ShoppingCart, key: "bulk_orders", roles: ["owner","admin"] },
  { to: "/care-portal/admin/inbox", label: "Inbox", icon: Inbox, key: "inbox", roles: ["owner","admin"] },
  { to: "/care-portal/admin/jobs", label: "Job Postings", icon: Briefcase, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/applications", label: "Applications", icon: UserSquare2, key: "applications_new", roles: ["owner","admin"] },
  { to: "/care-portal/admin/academy-applicants", label: "Academy Applicants", icon: GraduationCap, key: "academy_new", roles: ["owner","admin"] },
  { to: "/care-portal/admin/academy-portal", label: "Academy Portal", icon: GraduationCap, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/support-requests", label: "Care Portal", icon: ShieldCheck, key: "care_portal", roles: ["owner","admin","chaplain"] },
  { to: "/care-portal/admin/care-coordination", label: "Care Coordination", icon: HeartHandshake, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/activity-reports", label: "Activity Reports", icon: BarChart3, key: null, roles: ["owner","admin","chaplain"] },
  { to: "/care-portal/admin/organizations", label: "Organizations", icon: Building2, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/employer-utilization", label: "Employer Utilization", icon: PieChart, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/team", label: "Team", icon: Users, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/demo-submissions", label: "Demo Submissions", icon: Sparkles, key: null, roles: ["owner","admin"] },
  { to: "/care-portal/admin/demo-settings", label: "Demo Settings", icon: Settings, key: null, roles: ["owner","admin"] },
];

export default function AdminLayout() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);

  const load = () => {
    if (user?.role === "chaplain") { setOverview(null); return; }
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (user?.must_change_password) {
      navigate("/care-portal/admin/change-password", { replace: true });
      return;
    }
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user?.role, user?.must_change_password]);

  const handleLogout = async () => { await logout(); navigate("/care-portal/admin/login"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-layout">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Admin</p>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">CCCHAPS Management Center</h1>
          <p className="mt-1 text-sm text-slate-500">Signed in as {user?.email}</p>
        </div>
        <div className="flex items-center gap-2 self-start flex-wrap">
          {user && (
            <span data-testid="admin-identity-pill" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs text-slate-700">
              <span className="font-semibold text-slate-900">{user.name || user.email}</span>
              <span className="text-slate-400">·</span>
              <span data-testid="admin-identity-role" className={`px-1.5 py-0.5 rounded uppercase tracking-wider text-[10px] font-semibold ${user.role === "owner" ? "bg-[#4A6FA5]/10 text-[#3B597B]" : user.role === "chaplain" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>{user.role || "admin"}</span>
            </span>
          )}
          <button onClick={handleLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <nav className="bg-white border border-slate-200 rounded-2xl p-3 h-fit sticky top-24" data-testid="admin-sidebar">
          {NAV.filter((n) => !n.roles || (user?.role && n.roles.includes(user.role))).map((n) => {
            let count = 0;
            if (n.key === "applications_new") count = overview?.careers?.applications?.new || 0;
            else if (n.key === "academy_new") count = overview?.academy?.new || 0;
            else if (n.key && overview) count = overview[n.key]?.new || 0;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={({ isActive }) => `flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-medium mb-1 ${isActive ? "bg-[#F0F4F8] text-[#3B597B]" : "text-slate-700 hover:bg-slate-50"}`}>
                <span className="flex items-center gap-2.5"><n.icon className="w-4 h-4" strokeWidth={1.75} /> {n.label}</span>
                {count > 0 && <span data-testid={`admin-nav-badge-${n.key}`} className="inline-flex items-center justify-center min-w-[22px] px-1.5 h-5 rounded-full bg-[#4A6FA5] text-white text-[11px] font-semibold">{count}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="min-w-0"><Outlet context={{ overview, refreshOverview: load }} /></div>
      </div>
    </div>
  );
}
